import {
  ApiReportReportTypeChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ReportSectionsType,
} from "@api/graphql/types-and-hooks";
import {
  useIsReportBaseTemplate,
  useIsReportCompleted,
  useIsReportHandlebars,
  useIsReviewPage,
} from "@components/ReportField/capabilities/flags";
import { APPENDIX_PAGE_LOCATION, RECOMMENDATIONS_PAGE_LOCATION } from "@components/ReportField/consts";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";

// Whether the "Add Section" affordance shows. Consolidates the legacy per-surface
// gating (evaluation Evaluation*.tsx pages + clinical SectionWidgetWrapper `canAddSection`):
//   assessment  (InitialAssessment)          -> handlebars export
//   evaluation  (DiagnosisEvaluation + step) -> base template, appendix, or non-special-org recommendations
//   clinical    (Reassessment | DE + single) -> handlebars, or single-page appendix/non-special-org recommendations
// Special orgs (AT4K, Catalight) cannot add recommendation sections.
export const useCanAddSection = (section: ReportSectionsType): boolean => {
  const { report, viewMode } = useReport();
  const { isCatalightUser, isAT4KUser } = useAdminData();
  const isReviewPage = useIsReviewPage();
  const isCompleted = useIsReportCompleted();
  const isHandlebars = useIsReportHandlebars();
  const isBaseTemplate = useIsReportBaseTemplate();

  if (isCompleted) return false;
  if (isReviewPage) return false;

  const reportType = report.reportType;
  const onAppendix = section.pageLocation === APPENDIX_PAGE_LOCATION;
  const onRecommendations = section.pageLocation === RECOMMENDATIONS_PAGE_LOCATION;
  const isSpecialOrg = isAT4KUser || isCatalightUser;
  const sectionStillGenerating =
    section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending;

  if (sectionStillGenerating) return false;

  if (reportType === ApiReportReportTypeChoices.InitialAssessment) {
    return isHandlebars;
  }
  if (reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "step") {
    return isBaseTemplate || onAppendix || (onRecommendations && !isSpecialOrg);
  }

  // clinical (Reassessment | Diagnosis Evaluation single-page)
  if (isHandlebars) return true;
  const isDiagnosisSinglePage = reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && viewMode === "single";
  if (isDiagnosisSinglePage && onAppendix) return true;
  if (isDiagnosisSinglePage && onRecommendations) return !isSpecialOrg;
  return false;
};
